import React, { useEffect, useState, useRef, useTransition } from 'react';
import Footer from './components/Footer';
import Header from './components/Header';
import SearchBox from './components/SearchBox';
import SectionCards from './components/SectionCards';
import Carousel from './components/Carousel';
import Alert from './components/Alert';
import Summary from './components/Summary';
import Dropdown from './components/Dropdown';
import MaintenancePage from './components/MaintenancePage';
import LoginPrompt from './components/LoginPrompt';
import { useDebounce } from 'use-debounce';
import funFictionalCourses from './data/funFictionalCourses'; // Importing fictional courses data for testing
import combined from './data/combined.json'; // Importing combined.json for testing

function App() {

  const [isPending, startTransition] = useTransition();

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [matrixMode, setMatrixMode] = useState(false);
  const matrixIntervalRef = useRef(null);

  // Easter Egg state
  const [easterEggActive, setEasterEggActive] = useState(false);
  const [easterEggMessage, setEasterEggMessage] = useState(null);
  const [doomMode, setDoomMode] = useState(false);

  // Helper function to clear all filters
  const clearAllFilters = () => {
    startTransition(() => {
      setSelectedSubjectCode([]);
      setSelectedLocation([]);
      setSelectedPtrm([]);
      setSelectedInstructor([]);
      setSelectedAttribute([]);
      setSelectedDaysOfWeek([]);
      setSelectedStartTime('');
      setSelectedEndTime('');
      setSelectedScheduleCode([]);
      setSelectedDivision([]);
      setCourseNumberSearch('');
      setFilterZTCOnly(false);
      setFilterOpenClasses(false);
      setFilterWaitlistClasses(false);
      setFilterCredit(false);
      setFilterNonCredit(false);
    });
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const [selectedSubjectCode, setSelectedSubjectCode] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedPtrm, setSelectedPtrm] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState([]);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedDivision, setSelectedDivision] = useState([]);
  const [sectionData, setSectionsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBoxText, setSearchBoxText] = useState('');
  const [debouncedSearch] = useDebounce(searchBoxText, 300);
  const [filterZTCOnly, setFilterZTCOnly] = useState(false);
  const [filterOpenClasses, setFilterOpenClasses] = useState(false);
  const [filterWaitlistClasses, setFilterWaitlistClasses] = useState(false);
  const [filterCredit, setFilterCredit] = useState(false);
  const [filterNonCredit, setFilterNonCredit] = useState(false);
  const [ufoActive, setUfoActive] = useState(false);
  const [ufoTop, setUfoTop] = useState('50vh');
  const [ufoDirection, setUfoDirection] = useState('ltr'); // 'ltr' (left→right) or 'rtl' (right→left)
  const [layoutType, setLayoutType] = useState('card');

  // State variables for filter dropdowns
  const [subjectCodeOptions, setSubjectCodeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [ptrmOptions, setPtrmOptions] = useState([]);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  // --- SSRMEET Schedule Code filter state ---
  const [selectedScheduleCode, setSelectedScheduleCode] = useState([]);
  const [scheduleCodeOptions, setScheduleCodeOptions] = useState([]);
  const [scheduleDescToCodeMap, setScheduleDescToCodeMap] = useState({});
  // --- Course Number Search state ---
  const [courseNumberSearch, setCourseNumberSearch] = useState('');
  // Session Type
  const [sessCodeOptions, setSessCodeOptions] = useState([]);
  const [selectedSessCode, setSelectedSessCode] = useState([]);
  // Divison
  const [divisions, setDivisions] = useState([]);

  const fetchWithRetry = async (url, options = {}, retries = 5, delay = 1500) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return await res.json();
      } catch (error) {
        if (i < retries - 1) await new Promise(res => setTimeout(res, delay));
        else throw error;
      }
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const authStatus = sessionStorage.getItem('authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  useEffect(() => {
    if (selectedTerm) {
      setLoading(true);
      setSectionsData([]);
      setFilteredData([]);


      fetchWithRetry(`${apiUrl}/classSchedule/${selectedTerm}`)

        .then((data) => {

          setSectionsData(data);
          setLoading(false);
        })
        .catch(error => {
          console.error("Failed to fetch section data after retries:", error);
          setSectionsData([]);
          setLoading(false);
        });
    } else {
      startTransition(() => {
        setSectionsData([]);
        setFilteredData([]);
        // Clear dependent dropdown options
        setSubjectCodeOptions([]);
        setSessCodeOptions([]);
        setLocationOptions([]);
        setPtrmOptions([]);
        setInstructorOptions([]);
        setAttributeOptions([]);
        setSelectedDivision([]);
        setLoading(false);

        clearAllFilters();
      });
    }
  }, [selectedTerm, apiUrl]);

  // Populate dropdown options from sectionData
  useEffect(() => {
    // Ensure sectionData is not null or undefined before mapping
    if (sectionData && sectionData.length > 0) {
      const subjectCodes = [...new Set(sectionData.map(item => `${item.subjCode || ''} - ${item.subjDesc || ''}`.trim()).filter(Boolean))].sort();
      const locations = [...new Set(sectionData.map(item => item.campDesc).filter(Boolean))].sort();
      const ptrms = [...new Set(sectionData.map(item => item.ptrmDesc).filter(Boolean))].sort();
      const sessCodes = [...new Set(sectionData.map(item => item.sessDesc).filter(Boolean))].sort();
      const divisionData = [...new Set(sectionData.map(item => item.division).filter(Boolean))].sort();
      const instructors = [...new Set(
        sectionData.map(item => {
          const parts = item.pfaName.trim().split(/\s+/);
          const lastName = parts[parts.length - 1];
          const firstNames = parts.slice(0, -1).join(' ');
          return `${lastName}, ${firstNames}`;
        }).filter(Boolean)
      )].sort();
      const attributes = [...new Set(sectionData.map(item => item.saprDesc).filter(Boolean))].sort();
      // SSRMEET Schedule Code options - map descriptions to their codes
      const scheduleCodesMap = new Map();
      const descToCodeMapping = {};
      sectionData.forEach(item => {
        if (item.schdDesc && item.schdCode) {
          scheduleCodesMap.set(item.schdDesc, item.schdCode);
          descToCodeMapping[item.schdDesc] = item.schdCode;
        }
      });
      const scheduleCodes = Array.from(scheduleCodesMap.keys()).sort();

      startTransition(() => {
        setSubjectCodeOptions(subjectCodes);
        setLocationOptions(locations);
        setPtrmOptions(ptrms);
        setSessCodeOptions(sessCodes);
        setInstructorOptions(instructors);
        setAttributeOptions(attributes);
        setScheduleCodeOptions(scheduleCodes);
        setScheduleDescToCodeMap(descToCodeMapping);
        setDivisions(divisionData);
      });
    } else {
      // If sectionData is empty, clear the options
      startTransition(() => {
        setSubjectCodeOptions([]);
        setLocationOptions([]);
        setPtrmOptions([]);
        setInstructorOptions([]);
        setAttributeOptions([]);
        setScheduleCodeOptions([]);
        setScheduleDescToCodeMap({});
        setSessCodeOptions([]);
        setDivisions([]);
      });
    }
  }, [sectionData]);


  useEffect(() => {
    if (!sectionData || sectionData.length === 0) {
      setFilteredData({});
      return;
    }
    let filtered = sectionData;


    // First, group by CRN to handle schedule type filter properly
    const groupedByCRN = filtered.reduce((acc, item) => {
      if (!acc[item.crn]) {
        acc[item.crn] = [];
      }
      acc[item.crn].push(item);
      return acc;
    }, {});

    // Apply filters that should work at CRN level first
    let filteredCRNs = Object.keys(groupedByCRN);

    // SSRMEET Schedule Code filter - if ANY section in CRN matches, include ALL sections
    if (selectedScheduleCode && selectedScheduleCode.length > 0) {
      // Convert selected descriptions to codes
      const selectedCodes = selectedScheduleCode.map(desc => scheduleDescToCodeMap[desc]).filter(Boolean);
      filteredCRNs = filteredCRNs.filter(crn =>
        groupedByCRN[crn].some(item => selectedCodes.includes(item.schdCode))
      );
    }

    // Rebuild filtered array with complete CRN groups
    const filteredWithCRNGroups = filteredCRNs.flatMap(crn => groupedByCRN[crn]);

    // Apply remaining filters at section level
    filtered = filteredWithCRNGroups;

    // Subject Code filter
    if (selectedSubjectCode && selectedSubjectCode.length > 0) {
      filtered = filtered.filter(item => {
        const formattedCode = `${item.subjCode || ''} - ${item.subjDesc || ''}`.trim();
        return selectedSubjectCode.includes(formattedCode);
      });
    }

    // Course Number search filter
    if (courseNumberSearch.trim() !== '') {
      const query = courseNumberSearch.toLowerCase();
      filtered = filtered.filter(item => item.courseNumbDisplay?.toLowerCase().includes(query));
    }

    // Location filter
    if (selectedLocation && selectedLocation.length > 0) {
      filtered = filtered.filter(item => item.campDesc && selectedLocation.includes(item.campDesc));
    }

    // Division filter
    if (selectedDivision && selectedDivision.length > 0) {
      filtered = filtered.filter(item => item.division && selectedDivision.includes(item.division));
    }

    // Sess Code filter
    if (selectedSessCode && selectedSessCode.length > 0) {
      filtered = filtered.filter(item => item.sessDesc && selectedSessCode.includes(item.sessDesc));
    }

    // schedule type filter
    if (selectedPtrm && selectedPtrm.length > 0) {
      filtered = filtered.filter(item => item.ptrmDesc && selectedPtrm.includes(item.ptrmDesc));
    }

    // Instructor filter
    if (selectedInstructor && selectedInstructor.length > 0) {
      filtered = filtered.filter(item => {
        const parts = item.pfaName.trim().split(/\s+/);
        const lastName = parts[parts.length - 1];
        const firstNames = parts.slice(0, -1).join(' ');

        const instructorName = `${lastName}, ${firstNames}`;
        return instructorName && selectedInstructor.includes(instructorName);
      });
    }

    // Attribute filter
    if (selectedAttribute && selectedAttribute.length > 0) {
      filtered = filtered.filter(item => {
        const attributes = item.attrchrts ? item.attrchrts.split(',').map(a => a.trim()) : [];
        return selectedAttribute.every(attr => attributes.includes(attr));
      });
    }

    // Days of Week filter
    if (selectedDaysOfWeek && selectedDaysOfWeek.length > 0) {
      filtered = filtered.filter(item => {
        const days = [item.monDay, item.tueDay, item.wedDay, item.thuDay, item.friDay, item.satDay, item.sunDay]
          .filter(Boolean) // Remove null/undefined days
          .map(d => d.trim().toUpperCase());
        return days.length > 0 && selectedDaysOfWeek.some(day => days.includes(day));
      });
    }

    // Start and End Time filter
    if ((selectedStartTime && selectedStartTime !== '') || (selectedEndTime && selectedEndTime !== '')) {
      filtered = filtered.filter(item => {
        const classStartValid = item.beginTime && /^\d{4}$/.test(item.beginTime);
        const classEndValid = item.endTime && /^\d{4}$/.test(item.endTime);

        const classStart = classStartValid ? parseInt(item.beginTime, 10) : null;
        const classEnd = classEndValid ? parseInt(item.endTime, 10) : null;

        const startFilter = selectedStartTime && selectedStartTime !== '' ? parseInt(selectedStartTime.replace(':', ''), 10) : null;
        const endFilter = selectedEndTime && selectedEndTime !== '' ? parseInt(selectedEndTime.replace(':', ''), 10) : null;

        let startTimeMatches = true;
        if (startFilter !== null) {
          startTimeMatches = classStart !== null && classStart >= startFilter;
        }

        let endTimeMatches = true;
        if (endFilter !== null) {
          endTimeMatches = classEnd !== null && classEnd <= endFilter;
        }

        return startTimeMatches && endTimeMatches;
      });
    }

    // Zero Textbook Costs filter
    if (filterZTCOnly) {
      filtered = filtered.filter(item =>
        item.attrchrts && item.attrchrts.split(',').map(a => a.trim()).includes('ZTC')
      );
    }

    // Open Classes and Waitlist Classes filters
    if (filterOpenClasses || filterWaitlistClasses) {
      filtered = filtered.filter(item => {
        const status = item.fwSectOpenInd;
        return (filterOpenClasses && status === 'O') || (filterWaitlistClasses && status === 'W');
      });
    }

    // Credit and Non-Credit filters
    if (filterCredit) {
      filtered = filtered.filter(item => parseFloat(item.creditHrLow) > 0);
    }
    if (filterNonCredit) {
      filtered = filtered.filter(item => parseFloat(item.creditHrLow) === 0);
    }

    // Search Box filter
    if (debouncedSearch && debouncedSearch.trim() !== "") {
      const query = debouncedSearch.toLowerCase();
      const terms = query.split(/\s+/).filter(Boolean);

      filtered = filtered.filter(item => {
        const valuesToMatch = [
          item.courseTitle?.toLowerCase(),
          item.subjCode?.toLowerCase(),
          item.subjDesc?.toLowerCase(),
          item.courseNumbDisplay?.toLowerCase(),
          item.crn && String(item.crn)
        ].filter(Boolean).join(' ');

        return terms.every(term => valuesToMatch.includes(term));
      });
    }

    // Excessive filter selection
    const excessiveFilters = [
      selectedSubjectCode.length > 20,
      selectedInstructor.length > 10,
      selectedLocation.length > 5,
      selectedDaysOfWeek.length === 7
    ];

    // Sort and group the filtered data
    const sorted = [...filtered].sort((a, b) => {
      const subjA = a.subjCode || '';
      const subjB = b.subjCode || '';
      if (subjA !== subjB) return subjA.localeCompare(subjB);

      const extractParts = (str) => {
        if (!str) return [1, '', 0, ''];
        const match = str.match(/^([A-Z]*)(\d+)([A-Z]*)$/i);
        if (!match) return [1, '', 0, str];
        const [, prefix, digits, suffix] = match;
        const isAlphaPrefix = /^[A-Z]/i.test(str) ? 0 : 1;
        return [isAlphaPrefix, prefix || '', parseInt(digits), suffix || ''];
      };

      const [flagA, prefixA, numA, suffixA] = extractParts(a.courseNumbDisplay || '');
      const [flagB, prefixB, numB, suffixB] = extractParts(b.courseNumbDisplay || '');

      if (flagA !== flagB) return flagA - flagB;
      if (prefixA !== prefixB) return prefixA.localeCompare(prefixB);
      if (numA !== numB) return numA - numB;
      return suffixA.localeCompare(suffixB);
    });

    // Group by course for proper display
    const groupedByCourse = sorted.reduce((acc, item) => {
      const key = `${item.subjCode || 'N/A'} ${item.courseNumbDisplay || 'N/A'} - ${item.courseTitle || 'Untitled Course'}`;
      if (!acc[key]) {
        acc[key] = {
          description: item.description,
          sectionNote: item.sectionNote,
          courseNumbDisplay: item.courseNumbDisplay,
          crnGroups: {},
          subjDesc: item.subjDesc || 'N/A',
        };
      }
      if (!acc[key].crnGroups[item.crn]) {
        acc[key].crnGroups[item.crn] = [];
      }
      acc[key].crnGroups[item.crn].push(item);
      return acc;
    }, {});

    // Group by subject
    const subjectGroupedData = {};
    Object.entries(groupedByCourse).forEach(([courseKey, group]) => {
      const subjDesc = group.subjDesc || 'N/A';
      if (!subjectGroupedData[subjDesc]) {
        subjectGroupedData[subjDesc] = {};
      }
      subjectGroupedData[subjDesc][courseKey] = group;
    });

    setFilteredData(subjectGroupedData);
  }, [sectionData, selectedSubjectCode, selectedLocation, selectedSessCode, selectedDivision, selectedPtrm, selectedInstructor, selectedAttribute, selectedScheduleCode, scheduleDescToCodeMap, selectedDaysOfWeek, selectedStartTime, selectedEndTime, debouncedSearch, filterZTCOnly, filterOpenClasses, filterWaitlistClasses, filterCredit, filterNonCredit, courseNumberSearch, easterEggActive, easterEggMessage]);


  // Check if the app is in maintenance mode
  const isMaintenanceMode = false;

  // If in maintenance mode, render the MaintenancePage component
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return <LoginPrompt onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className={`flex flex-col min-h-screen bg-pcc-lighter-gray ${matrixMode ? 'matrix-mode' : ''}`}>


      <Header className="bg-pcc-cardinal text-white p-4" />
      {/* <Carousel /> */}


      {/* 🔄 Layout Switcher */}
      {/* <div className="fixed top-24 right-4 z-50 bg-white/90 backdrop-blur border rounded-md shadow p-2">
        <select
          value={layoutType}
          onChange={(e) => setLayoutType(e.target.value)}
          className="text-sm border-gray-300 rounded-md px-2 py-1 focus:ring-pcc-cardinal focus:border-pcc-cardinal"
        >
          <option value="card">Default Cards</option>
          <option value="columns">Columns</option>
          <option value="centric">CRN-Centric</option>
        </select>
      </div> */}

      {/* --- TOP FILTERS SECTION --- */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mt-6 px-4 w-full max-w-screen-lg mx-auto">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">Class Schedule for</span>
          <div className="min-w-[220px]">
            <Dropdown
              title="Term"
              fetchUrl={`${apiUrl}/terms`}
              resultKey="academicPeriods16"
              valueField="code"
              labelField="title"

              onFilterSelect={setSelectedTerm}
              isMulti={false}
            />
          </div>
        </div>
        <div className="h-10 w-px bg-gray-300 hidden sm:block" />
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-sm text-gray-600">Search</span>
          <div className="w-full sm:w-[400px] md:w-[500px] lg:w-[600px]">
            <SearchBox onSearch={setSearchBoxText} />
          </div>
        </div>
      </div>
      {/* --- END TOP FILTERS SECTION --- */}


      {selectedTerm && (
        <>
          <div className="px-4 w-full max-w-screen-lg mx-auto mt-6 space-y-6">
            {/* Course Info */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Course Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Subject (formerly Subject Code), Course Number, Instructor, Term Duration */}
                <div>
                  <div className="font-semibold text-sm mb-1">Subject</div>
                  <Dropdown title="Subject" options={subjectCodeOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedSubjectCode} isMulti={true} />
                </div>
                {/* Course Number Search Input */}
                <div>
                  <div className="font-semibold text-sm mb-1">Course Number</div>
                  <input
                    type="text"
                    placeholder="e.g. 001B"
                    value={courseNumberSearch}
                    onChange={(e) => setCourseNumberSearch(e.target.value)}
                    className="w-full h-11 border rounded-md px-3 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-pcc-gold"
                  />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Instructor</div>
                  <Dropdown title="Instructor" options={instructorOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedInstructor} isMulti={true} />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Division</div>
                  <Dropdown title="Division" options={divisions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedDivision} isMulti={true} />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">Term Duration</div>
                  <Dropdown title="Term" options={ptrmOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedPtrm} isMulti={true} />
                </div>
              </div>
            </div>

            {/* Logistics */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Logistics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Location/Campus */}
                <div>
                  <div className="font-semibold text-sm mb-1">Location/Campus</div>
                  <Dropdown title="Location" options={locationOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedLocation} isMulti={true} />
                </div>
                {/* Schedule Type */}
                <div>
                  <div className="font-semibold text-sm mb-1">Schedule Type</div>
                  <Dropdown title="Schedule Type" options={scheduleCodeOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedScheduleCode} isMulti={true} />
                </div>
                {/* Session Type */}
                <div>
                  <div className="font-semibold text-sm mb-1">Sessions Type</div>
                  <Dropdown title="Session Type" options={sessCodeOptions.map(x => ({ label: x, value: x }))} onFilterSelect={setSelectedSessCode} isMulti={true} />
                </div>
                {/* Day of Week */}
                <div>
                  <div className="font-semibold text-sm mb-1">Day of Week</div>
                  <Dropdown title="Days" options={[{ label: 'Mon', value: 'M' }, { label: 'Tue', value: 'T' }, { label: 'Wed', value: 'W' }, { label: 'Thu', value: 'R' }, { label: 'Fri', value: 'F' }, { label: 'Sat', value: 'S' }, { label: 'Sun', value: 'U' }]} onFilterSelect={setSelectedDaysOfWeek} isMulti={true} />
                </div>
                {/* Start Time */}
                <div>
                  <div className="font-semibold text-sm mb-1">Start Time</div>
                  <Dropdown title="Start Time" options={[{ label: '8:00 AM', value: '08:00' }, { label: '9:00 AM', value: '09:00' }, { label: '10:00 AM', value: '10:00' }, { label: '11:00 AM', value: '11:00' }, { label: '12:00 PM', value: '12:00' }, { label: '1:00 PM', value: '13:00' }, { label: '2:00 PM', value: '14:00' }, { label: '3:00 PM', value: '15:00' }, { label: '4:00 PM', value: '16:00' }, { label: '5:00 PM', value: '17:00' }, { label: '6:00 PM', value: '18:00' }]} onFilterSelect={val => setSelectedStartTime(Array.isArray(val) ? (val[0] || '') : (val || ''))} isMulti={false} />
                </div>
                {/* End Time */}
                <div>
                  <div className="font-semibold text-sm mb-1">End Time</div>
                  <Dropdown title="End Time" options={[{ label: '9:00 AM', value: '09:00' }, { label: '10:00 AM', value: '10:00' }, { label: '11:00 AM', value: '11:00' }, { label: '12:00 PM', value: '12:00' }, { label: '1:00 PM', value: '13:00' }, { label: '2:00 PM', value: '14:00' }, { label: '3:00 PM', value: '15:00' }, { label: '4:00 PM', value: '16:00' }, { label: '5:00 PM', value: '17:00' }, { label: '6:00 PM', value: '18:00' }, { label: '7:00 PM', value: '19:00' }]} onFilterSelect={val => setSelectedEndTime(Array.isArray(val) ? (val[0] || '') : (val || ''))} isMulti={false} />
                </div>

              </div>
            </div>

            {/* Attributes */}
            <div>
              <h3 className="text-md font-semibold text-pcc-dark-gray mb-2">Attributes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_2fr] gap-4">
                <div className="flex flex-col justify-between h-full">
                  <div>
                    <div className="font-semibold text-sm mb-1">Attribute</div>
                    <Dropdown
                      title="Attribute"
                      fetchUrl={`${apiUrl}/courseCategories`}
                      resultKey="courseCategories11"
                      valueField="code"
                      labelField="title"
                      onFilterSelect={setSelectedAttribute}
                      isMulti={true}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Zero Textbook Costs */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Zero Textbook Costs</div>
                      <div className="flex items-center mt-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="ztcFilter"
                            checked={filterZTCOnly}
                            onChange={(e) => setFilterZTCOnly(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          ZTC Only Classes
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Open Class */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Status</div>
                      <div className="flex flex-col mt-1 space-y-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="openClasses"
                            checked={filterOpenClasses}
                            onChange={(e) => setFilterOpenClasses(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Open
                        </label>
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="waitlistClasses"
                            checked={filterWaitlistClasses}
                            onChange={(e) => setFilterWaitlistClasses(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Waitlist
                        </label>
                      </div>
                    </div>
                  </div>


                  {/* Class Type */}
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="font-semibold text-sm mb-1">Class Type</div>
                      <div className="flex flex-col mt-1 space-y-1">
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="creditFilter"
                            checked={filterCredit}
                            onChange={(e) => setFilterCredit(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Credit
                        </label>
                        <label className="inline-flex items-center text-sm">
                          <input
                            type="checkbox"
                            id="nonCreditFilter"
                            checked={filterNonCredit}
                            onChange={(e) => setFilterNonCredit(e.target.checked)}
                            className="form-checkbox h-4 w-4 text-pcc-cardinal rounded mr-2" // Added Tailwind form classes
                          />
                          Noncredit
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-between h-full">
                    <div className="text-lg text-gray-600">
                      Total Class(es): {Object.values(filteredData).reduce((total, subject) =>
                        total + Object.values(subject).reduce((subjectTotal, course) =>
                          subjectTotal + Object.keys(course.crnGroups || {}).length, 0), 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex mt-6">
        <SectionCards data={filteredData} loading={loading} selectedTerm={selectedTerm} layoutType={layoutType} />
      </div>
      <Footer className="bg-pcc-cardinal text-white p-4 mt-auto" />

    </div>
  );
}

export default App;